import type { ChipRefAttrs } from "../rich-chip-editor/chip-extension";
import type { ChipStateGetter } from "../rich-chip-editor/chip-state";
import { smartLinkStyles } from "@quovy/types/chip-doc/smart-links";
import type {
  EntityRefStyle,
  EntityRefType,
  EntityTaggableType,
} from "@quovy/types/chip-doc/entity-refs";

const STAGED_FOLDER_STYLE: EntityRefStyle = {
  // lucide "folder-input" — a mapped folder, styled distinctly from a concrete file.
  color: "#be185d",
  background: "oklch(0.59 0.2 5 / 0.12)",
  iconSvg:
    "%3Cpath d='M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-1'/%3E%3Cpath d='M2 13h10'/%3E%3Cpath d='m9 16 3-3-3-3'/%3E",
};

const TAGGABLE_STYLES: Record<EntityTaggableType, EntityRefStyle> = {
  staged_folder: STAGED_FOLDER_STYLE,
  agent: {
    color: "var(--primary)",
    background: "oklch(from var(--primary) l c h / 0.1)",
    iconSvg:
      "%3Cpath d='M12 8V4H8'/%3E%3Crect width='16' height='12' x='4' y='8' rx='2'/%3E%3Cpath d='M2 14h2'/%3E%3Cpath d='M20 14h2'/%3E%3Cpath d='M15 13v2'/%3E%3Cpath d='M9 13v2'/%3E",
  },
  tool: {
    color: "var(--primary)",
    background: "oklch(from var(--primary) l c h / 0.12)",
    iconSvg:
      "%3Cpath d='M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'/%3E",
  },
  file: {
    color: "#0e7490",
    background: "oklch(0.6 0.13 215 / 0.14)",
    iconSvg:
      "%3Cpath d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z'/%3E%3Cpath d='M14 2v4a2 2 0 0 0 2 2h4'/%3E%3Cpath d='M10 9H8'/%3E%3Cpath d='M16 13H8'/%3E%3Cpath d='M16 17H8'/%3E",
  },
  // Runtime-defined data models (lucide "table-2").
  custom_model: {
    color: "#0369a1",
    background: "oklch(0.58 0.14 240 / 0.13)",
    iconSvg:
      "%3Cpath d='M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18'/%3E",
  },
  credential: {
    color: "#92400e",
    background: "oklch(0.65 0.15 65 / 0.14)",
    iconSvg:
      "%3Cpath d='m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4'/%3E%3Cpath d='m21 2-9.6 9.6'/%3E%3Ccircle cx='7.5' cy='15.5' r='5.5'/%3E",
  },
  skill: {
    color: "#7c3aed",
    background: "oklch(0.6 0.2 295 / 0.14)",
    iconSvg:
      "%3Cpath d='M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 16.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 20.39a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z'/%3E",
  },
  integration: {
    color: "#0f766e",
    background: "oklch(0.55 0.12 185 / 0.14)",
    iconSvg:
      "%3Cpath d='M12 2v8'/%3E%3Cpath d='M18 8v4a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8'/%3E%3Cpath d='M18 12h.01'/%3E%3Cpath d='M6 12h.01'/%3E%3Cpath d='m4.93 10.93 1.41 1.41'/%3E%3Cpath d='m17.07 10.93-1.41 1.41'/%3E",
  },
};

/** Chip styles for every {@link EntityRefType}; drives the injected CSS. */
export const ENTITY_REF_STYLES: Record<EntityRefType, EntityRefStyle> = {
  ...TAGGABLE_STYLES,
  // Retired chip keeps the staged-folder look so a legacy document still reads.
  port: STAGED_FOLDER_STYLE,
  ...smartLinkStyles(),
};

/** A taggable entity returned by the autocomplete source for the `@` popup. */
export interface EntityTaggable {
  id: string;
  type: EntityTaggableType;
  label: string;
  /**
   * Short metadata rendered as a trailing tag on the same line as the label
   * (a tool id, a credential kind, a field's type). Keep it to a couple of
   * words — it competes with the label for one row's width.
   */
  sublabel?: string;
  /**
   * Author-written prose about the entity, rendered on its OWN line under the
   * label. Distinct from `sublabel` because it is arbitrarily long: sharing a
   * line with it truncated a custom model's name down to "Cl…" next to its
   * description. Set this, not `sublabel`, for anything sentence-shaped.
   */
  description?: string;
  /**
   * For a `custom_model` taggable, the field key this row inserts as a field
   * chip. Absent means the row inserts a whole-model chip.
   */
  field?: string;
  /**
   * For a whole-model `custom_model` taggable, whether the model has fields
   * to drill into — drives the `→` affordance and whether ArrowRight on the
   * row opens the field list instead of inserting a chip.
   */
  drillable?: boolean;
}

export interface EntityChipEditorProps {
  /** Current value — Tiptap JSON string or plain text (legacy). */
  value?: string | null;
  /** Called with the serialized Tiptap JSON string on every edit. */
  onChange?: (value: string) => void;
  /** Placeholder text when the editor is empty. */
  placeholder?: string;
  /** Disable editing. */
  disabled?: boolean;
  /** Editor min-height in rows (default: 8). */
  rows?: number;
  /** Additional className for the wrapper. */
  className?: string;
  /**
   * Async source for the `@` popup. Given the current query, returns the
   * taggable entities to show. Omit to disable tagging (plain rich text only).
   */
  search?: (query: string) => Promise<EntityTaggable[]>;
  /**
   * Optional URL for an "Add integration" action rendered at the bottom of the
   * Integrations group in the `@` popup. Selecting it opens the URL in a new
   * tab. Omit to hide the action.
   */
  addIntegrationHref?: string;
  /**
   * When set, an `@` query that slugs to a valid, undeclared folder name
   * offers a "Create staged folder" row. The callback declares the folder in
   * the caller's draft config; the popup then inserts its chip.
   */
  onCreateStagedFolder?: (slug: string) => void;
  /** Called when the user clicks a chip (chips get a pointer cursor). What
   *  opens — a ChipPopover, a drawer, ... — is up to the caller. */
  onChipClick?: (attrs: ChipRefAttrs, element: HTMLElement) => void;
  /** Called when the pointer enters a chip. Pair with `onChipHoverEnd`; both
   *  come from `useChipTarget({ trigger: "hover" })` as `chipProps`. */
  onChipHover?: (attrs: ChipRefAttrs, element: HTMLElement) => void;
  /** Called when the pointer leaves the hovered chip. */
  onChipHoverEnd?: () => void;
  /** Per-chip visual state (e.g. `{ invalid: true }` → red + warning icon). */
  getChipState?: ChipStateGetter;
  /** Bump when the data behind `getChipState` changes, so decorations refresh. */
  chipStateKey?: unknown;
}

/**
 * Read-only entity chip rendering. Takes the same chip-interaction props as the
 * editor, so a surface can hang a {@link ChipPopover} off chips in published
 * text exactly as it does in the editor.
 */
export interface EntityChipContentProps extends Pick<
  EntityChipEditorProps,
  "className" | "onChipClick" | "onChipHover" | "onChipHoverEnd" | "getChipState"
> {
  /** The chip document to render. Accepts `undefined` (unlike the editor's
   *  `value`) — callers pass a not-yet-loaded field straight through. */
  value: string | null | undefined;
  /** Bump when the data behind `getChipState` changes, so decorations refresh. */
  chipStateKey?: unknown;
}
